import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Translate, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { ITag } from 'app/shared/model/tag.model';
import { getEntities as getTags } from 'app/entities/tag/tag.reducer';
import { IWebPage } from 'app/shared/model/web-page.model';
import { getEntity, updateEntity, createEntity, reset } from './web-page.reducer';

export const WebPageUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const account = useAppSelector(state => state.authentication.account);
  const tags = useAppSelector(state => state.tag.entities);
  const webPageEntity = useAppSelector(state => state.webPage.entity);
  const loading = useAppSelector(state => state.webPage.loading);
  const updating = useAppSelector(state => state.webPage.updating);
  const updateSuccess = useAppSelector(state => state.webPage.updateSuccess);

  const [formData, setFormData] = useState<any>({
    createdBy: account?.id,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleClose = () => {
    navigate('/web-page' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
      setFormData({
        createdBy: account?.id,
      });
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getTags({}));
  }, []);

  useEffect(() => {
    if (webPageEntity) {
      setFormData({
        ...webPageEntity,
        createdBy: webPageEntity?.createdBy?.id,
        tags: webPageEntity?.tags?.map(e => e.id.toString()),
      });
    }
  }, [webPageEntity]);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.code || formData.code.trim() === '') {
      newErrors.code = 'Code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleMultiSelectChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const renderMultiSelectValue = (selected: any[]) => {
    return selected?.join(', ') || '';
  };

  // eslint-disable-next-line complexity
  const saveEntity = () => {
    if (!validate()) {
      return;
    }

    const values = { ...formData };

    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }

    values.enabled = values.enabled === true;

    const entity = {
      ...webPageEntity,
      ...values,
      createdBy: users.find(it => it.id.toString() === values.createdBy?.toString()),
      tags: values.tags ? mapIdList(values.tags) : [],
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2">
          <Translate contentKey="jhipsterSampleApplicationApp.webPage.home.createOrEditLabel">Create or edit a WebPage</Translate>
        </Typography>
      </Box>
      <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }} noValidate autoComplete="off">
        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            {!isNew ? (
              <TextField id="web-page-id" name="id" label={translate('global.field.id')} value={formData.id || ''} fullWidth InputProps={{ readOnly: true }} />
            ) : null}
            <TextField
              label={translate('jhipsterSampleApplicationApp.webPage.code')}
              id="web-page-code"
              name="code"
              data-cy="code"
              value={formData.code || ''}
              onChange={handleChange('code')}
              fullWidth
              required
              error={!!errors.code}
              helperText={errors.code || ''}
            />
            <FormControlLabel
              control={
                <Switch
                  id="web-page-enabled"
                  name="enabled"
                  data-cy="enabled"
                  checked={formData.enabled || false}
                  onChange={handleSwitchChange('enabled')}
                />
              }
              label={translate('jhipsterSampleApplicationApp.webPage.enabled')}
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.webPage.uriPath')}
              id="web-page-uriPath"
              name="uriPath"
              data-cy="uriPath"
              value={formData.uriPath || ''}
              onChange={handleChange('uriPath')}
              fullWidth
            />
            <TextField
              label={translate('jhipsterSampleApplicationApp.webPage.publishDate')}
              id="web-page-publishDate"
              name="publishDate"
              data-cy="publishDate"
              value={formData.publishDate || ''}
              onChange={handleChange('publishDate')}
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth>
              <InputLabel id="web-page-tags-label">{translate('jhipsterSampleApplicationApp.webPage.tags')}</InputLabel>
              <Select
                labelId="web-page-tags-label"
                id="web-page-tags"
                data-cy="tags"
                multiple
                value={formData.tags || []}
                label={translate('jhipsterSampleApplicationApp.webPage.tags')}
                onChange={handleMultiSelectChange('tags')}
                renderValue={renderMultiSelectValue}
              >
                {tags
                  ? tags.map(otherEntity => (
                      <MenuItem value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.code}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                component={Link}
                id="cancel-save"
                data-cy="entityCreateCancelButton"
                to="/web-page"
                replace
                color="info"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon="arrow-left" />}
              >
                <Translate contentKey="entity.action.back">Back</Translate>
              </Button>
              <Button
                color="primary"
                id="save-entity"
                data-cy="entityCreateSaveButton"
                onClick={saveEntity}
                disabled={updating}
                variant="contained"
                startIcon={<FontAwesomeIcon icon="save" />}
              >
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WebPageUpdate;
